import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import AnimeCard from "@/components/base/AnimeCard";
import NewsCard from "@/components/base/NewsCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { animes } from "@/mocks/animes";
import { categories } from "@/mocks/categories";
import { filterAnimes, getCategoryMeta, searchAll, sortAnimes } from "@/lib/data";

const PAGE_SIZE = 20;
const YEAR_OPTIONS = ["2025", "2024", "2023", "2022", "2021"];
const RATING_OPTIONS = [
  { label: "全部评分", value: "" },
  { label: "8.0 分以上", value: "8" },
  { label: "8.5 分以上", value: "8.5" },
  { label: "9.0 分以上", value: "9" },
];
const SORT_OPTIONS = [
  { label: "推荐优先", value: "recommend" },
  { label: "最新播出", value: "newest" },
  { label: "评分最高", value: "rating" },
];

export default function CategoryPage() {
  const { categoryName = "all" } = useParams();
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get("q") || "").trim();

  const meta = getCategoryMeta(categoryName);
  const isAll = categoryName === "all";

  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [yearFilter, setYearFilter] = useState<string[]>([]);
  const [ratingFilter, setRatingFilter] = useState("");
  const [sort, setSort] = useState("recommend");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setTypeFilter([]);
    setYearFilter([]);
    setRatingFilter("");
    setSort("recommend");
    setPage(1);
  }, [categoryName, keyword]);

  const categoryTitle = isAll ? "全部动画" : `${categoryName}动画`;
  const categoryDesc =
    meta?.description || "汇集站内全部日本动画资源，按热度、评分与年份自由探索。";

  const { animeResults, newsResults } = useMemo(() => searchAll(keyword), [keyword]);

  const filtered = useMemo(() => {
    const base = keyword ? animeResults : animes;
    const scoped = isAll ? base : base.filter((item) => item.categories.includes(categoryName));
    const result = filterAnimes(scoped, {
      category: isAll ? undefined : categoryName,
      years: yearFilter,
      rating: ratingFilter,
    });
    const typed = typeFilter.length
      ? result.filter((item) => item.categories.some((c) => typeFilter.includes(c)))
      : result;
    return sortAnimes(typed, sort);
  }, [isAll, categoryName, yearFilter, ratingFilter, typeFilter, sort, keyword, animeResults]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const pageTitle = keyword
    ? `日漫 - “${keyword}” 搜索结果`
    : `日漫 - ${categoryTitle} 列表`;

  useDocumentMeta({
    title: pageTitle,
    description: keyword
      ? `在日漫搜索“${keyword}”的相关动画与资讯结果，共找到 ${filtered.length} 部动画与 ${newsResults.length} 篇资讯。`
      : `日漫 ${categoryTitle} 专题：${categoryDesc}`,
    keywords: `${categoryTitle},日漫,日本动画,动漫资讯`,
  });

  const toggleValue = (list: string[], value: string, setter: (v: string[]) => void) => {
    setter(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
    setPage(1);
  };

  const resetFilters = () => {
    setTypeFilter([]);
    setYearFilter([]);
    setRatingFilter("");
    setSort("recommend");
    setPage(1);
  };

  const hasActiveFilter = typeFilter.length > 0 || yearFilter.length > 0 || ratingFilter !== "";

  return (
    <Layout>
      <div className="w-full px-4 md:px-6 pt-6">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "动画分类", to: "/category/all" },
            { label: keyword ? "搜索结果" : categoryTitle },
          ]}
        />
      </div>

      <section className="w-full px-4 md:px-6 pt-6 pb-4">
        <div className="rounded-lg bg-secondary-50 border border-secondary-100 p-6 md:p-8">
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
            {keyword ? `“${keyword}” 的搜索结果` : categoryTitle}
          </h1>
          <p className="mt-2 text-sm text-foreground-600 max-w-3xl">
            {keyword
              ? `在动画名称、日文名与资讯标题中检索关键词，共匹配到 ${filtered.length} 部动画与 ${newsResults.length} 篇资讯。`
              : categoryDesc}
          </p>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 pb-16">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <aside className="w-full lg:w-64 shrink-0">
            <button
              type="button"
              onClick={() => setFiltersOpen((prev) => !prev)}
              className="lg:hidden w-full h-11 mb-3 rounded-md border border-background-200 bg-background-50 text-sm font-medium text-foreground-800 flex items-center justify-between px-4 cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-filter-3-line" />
                筛选与排序
                {hasActiveFilter && <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />}
              </span>
              <i className={filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
            </button>

            <div className={`${filtersOpen ? "block" : "hidden"} lg:block space-y-5`}>
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="font-heading text-sm font-bold text-foreground-950 mb-3">动画类型</h2>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.key}
                      type="button"
                      onClick={() => toggleValue(typeFilter, cat.key, setTypeFilter)}
                      className={`px-3 h-8 rounded-md text-xs border transition-colors cursor-pointer whitespace-nowrap ${
                        typeFilter.includes(cat.key)
                          ? "bg-primary-500 border-primary-500 text-background-50"
                          : "border-background-200 text-foreground-700 hover:border-primary-300"
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="font-heading text-sm font-bold text-foreground-950 mb-3">播出年份</h2>
                <div className="flex flex-wrap gap-2">
                  {YEAR_OPTIONS.map((year) => (
                    <button
                      key={year}
                      type="button"
                      onClick={() => toggleValue(yearFilter, year, setYearFilter)}
                      className={`px-3 h-8 rounded-md text-xs border transition-colors cursor-pointer whitespace-nowrap ${
                        yearFilter.includes(year)
                          ? "bg-primary-500 border-primary-500 text-background-50"
                          : "border-background-200 text-foreground-700 hover:border-primary-300"
                      }`}
                    >
                      {year} 年
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="font-heading text-sm font-bold text-foreground-950 mb-3">评分区间</h2>
                <div className="flex flex-col gap-2">
                  {RATING_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setRatingFilter(option.value);
                        setPage(1);
                      }}
                      className={`h-9 rounded-md text-xs border text-left px-3 transition-colors cursor-pointer ${
                        ratingFilter === option.value
                          ? "bg-secondary-500 border-secondary-500 text-background-50"
                          : "border-background-200 text-foreground-700 hover:border-secondary-300"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="font-heading text-sm font-bold text-foreground-950 mb-3">排序方式</h2>
                <div className="flex flex-col gap-2">
                  {SORT_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setSort(option.value);
                        setPage(1);
                      }}
                      className={`h-9 rounded-md text-xs px-3 text-left transition-colors cursor-pointer ${
                        sort === option.value
                          ? "bg-accent-100 text-accent-900 font-medium"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {hasActiveFilter && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="w-full h-10 rounded-md border border-primary-300 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
                >
                  <i className="ri-refresh-line" />
                  重置筛选条件
                </button>
              )}
            </div>
          </aside>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-4 text-sm text-foreground-600">
              <span>
                共 <strong className="text-foreground-950">{filtered.length}</strong> 部动画
                {keyword && newsResults.length > 0 && ` · ${newsResults.length} 篇相关资讯`}
              </span>
              <span className="hidden sm:block">
                第 {page} / {totalPages} 页
              </span>
            </div>

            {pageItems.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                {pageItems.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>
            ) : (
              <EmptyState
                title="没有找到符合条件的动画"
                description="试着调整筛选条件，或重置后重新浏览全部作品。"
                actionLabel="重置筛选条件"
                onAction={resetFilters}
              />
            )}

            <Pagination current={page} total={totalPages} onChange={setPage} />

            {keyword && newsResults.length > 0 && (
              <div className="mt-14">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-xl font-bold text-foreground-950">相关资讯</h2>
                  <Link
                    to="/news"
                    className="text-sm text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                  >
                    进入资讯中心
                  </Link>
                </div>
                <div className="flex flex-col gap-4">
                  {newsResults.map((news) => (
                    <NewsCard key={news.id} news={news} layout="horizontal" />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}